import * as React from "react";

import styles from "@/Chat/ChatSidebar/ChatByIdView/ChatMessage/AssistantTextContent.module.css";
import { AISummaryMarkdownLink } from "@/shared/components/ai/AISummaryMarkdownLink.tsx";
import { RobotoMarkdown } from "@/shared/components/RobotoMarkdown";
import { RobotoTypography } from "@/shared/components/RobotoTypography";

interface AssistantTextContentProps {
  text: string;
  isGenerating: boolean;
  onLinkClick?: () => void;
  autoNavEnabled: boolean;
  autoNavDedupeKey: string;
}

const AssistantTextContent = ({
  text,
  isGenerating,
  onLinkClick,
  autoNavEnabled,
  autoNavDedupeKey,
}: AssistantTextContentProps) => {
  const linkRenderers = React.useMemo(() => {
    const autoNavProps = {
      enabled: true,
      dedupeKey: autoNavDedupeKey,
    };

    // Helper to create link renderers with common auto-nav logic
    const createLinkRenderer = <T extends Record<string, unknown>>(
      props: T & { auto?: boolean; onLinkClick?: () => void; text: string },
    ) => {
      const { auto, ...restProps } = props;
      return (
        <AISummaryMarkdownLink
          {...restProps}
          auto={auto && autoNavEnabled}
          autoNavProps={autoNavProps}
        />
      );
    };

    return {
      datasetLinkRenderer: (props: {
        auto?: boolean;
        datasetId: string;
        onLinkClick?: () => void;
        text: string;
      }) => createLinkRenderer(props),
      fileLinkRenderer: (props: {
        auto?: boolean;
        fileId: string;
        onLinkClick?: () => void;
        text: string;
        time?: bigint;
      }) => createLinkRenderer(props),
      topicLinkRenderer: (props: {
        auto?: boolean;
        onLinkClick?: () => void;
        text: string;
        time?: bigint;
        topicId: string;
      }) => createLinkRenderer(props),
      messagePathLinkRenderer: (props: {
        auto?: boolean;
        messagePathId: string;
        onLinkClick?: () => void;
        text: string;
        time?: bigint;
      }) => createLinkRenderer(props),
    };
  }, [autoNavDedupeKey, autoNavEnabled]);

  return (
    <div className={styles.indentedContainer}>
      <div className={styles.messageContainer}>
        <RobotoMarkdown
          content={text}
          {...linkRenderers}
          onLinkClick={onLinkClick}
          variant={"compact"}
        />
        {isGenerating && (
          <RobotoTypography className={styles.generatingText}>
            Generating...
          </RobotoTypography>
        )}
      </div>
    </div>
  );
};

export { AssistantTextContent };
